import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────────

  public componentDidMount() {
    // Catch chunk errors that happen OUTSIDE React's render cycle
    // (e.g. dynamic imports triggered by React Router during navigation).
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ChunkErrorBoundary] Uncaught chunk or render error:", error, errorInfo);

    const errorMessage = error.message || "";
    const isChunkError =
      error.name === "ChunkLoadError" ||
      errorMessage.includes("Loading chunk") ||
      errorMessage.includes("Unexpected token '<'") ||
      errorMessage.includes("loading chunk") ||
      errorMessage.includes("Script error");

    if (isChunkError) {
      this.autoReloadOnChunkError();
    }
  }

  // ─── Global event handlers ────────────────────────────────────────────────────

  /**
   * Catches synchronous JS errors (e.g. SyntaxError when a chunk file returns
   * HTML instead of JavaScript because the server redirected to the login page).
   */
  private handleGlobalError = (event: ErrorEvent) => {
    const message = event.message || "";
    const filename = event.filename || "";

    const isChunkError =
      message.includes("Loading chunk") ||
      message.includes("ChunkLoadError") ||
      (message.includes("Unexpected token") && filename.includes(".chunk.")) ||
      filename.includes(".chunk.js");

    if (isChunkError) {
      console.warn("[ChunkErrorBoundary] Global chunk error detected:", message);
      this.autoReloadOnChunkError();
    }
  };

  /**
   * Catches promise-based chunk load failures (e.g. import() rejections that
   * are not caught inside a component and therefore never reach componentDidCatch).
   */
  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message: string = reason?.message || String(reason ?? "");

    const isChunkError =
      reason?.name === "ChunkLoadError" ||
      message.includes("Loading chunk") ||
      message.includes("ChunkLoadError");

    if (isChunkError) {
      console.warn("[ChunkErrorBoundary] Unhandled chunk rejection detected:", message);
      this.autoReloadOnChunkError();
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * Triggers a single automatic page reload to fetch fresh chunk assets.
   * A 10-second debounce stored in sessionStorage prevents infinite reload loops.
   */
  private autoReloadOnChunkError = () => {
    const retryKey = "chunk_load_retry_timestamp";
    const lastRetry = sessionStorage.getItem(retryKey);
    const now = Date.now();

    if (!lastRetry || now - parseInt(lastRetry, 10) > 10_000) {
      sessionStorage.setItem(retryKey, now.toString());
      console.warn("[ChunkErrorBoundary] Chunk load failed — auto-refreshing to load latest assets...");
      window.location.reload();
    }
  };

  private handleManualReload = () => {
    sessionStorage.removeItem("chunk_load_retry_timestamp");
    window.location.reload();
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex-1 min-h-[80vh] flex items-center justify-center p-6 bg-[#0B0B0B] text-white">
          <div className="max-w-md w-full bg-[#11131E] border border-gray-800 rounded-2xl p-8 text-center shadow-2xl relative overflow-hidden">
            {/* Ambient Purple Backlight */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="mx-auto w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>

            <h2 className="text-xl font-bold mb-3 tracking-tight">Application Load Failure</h2>
            <p className="text-neutral-medium text-sm mb-6 leading-relaxed">
              We encountered a connection issue while fetching the latest updates. Refreshing the application should resolve this immediately.
            </p>

            <button
              onClick={this.handleManualReload}
              className="w-full button-active flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-sm font-semibold transition-all hover:-translate-y-0.5 active:scale-95 shadow-md cursor-pointer"
            >
              <RefreshCw size={16} className="animate-spin duration-[3s]" />
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

